import styled from 'styled-components';
import Button from 'components/Button';
import { Form } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';
import Empty from 'components/Empty';
import { IPackageProgram } from 'features/settingService/services/types/packageSetting';
import DetailHeader from './DetailHeader';
import Text from 'components/Text';
import { calculateTier } from '../CreatePackageModal/PackageForm';
import PackageTierCard from '../CreatePackageModal/PackageTierCard';
import Add from 'assets/Icons/add';
import colorTheme from 'constants/color';
import Box from 'components/Box';
import settingApis from 'features/settingService/services/apis';
import { getMessageError, IResponseDataBody } from 'services/response';
import toast from 'utils/toast';
import { PACKAGE_TYPE } from 'features/settingService/services/constants';
import CreatePackageModal, { CreatePackageModalRef } from '../CreatePackageModal';
import useGetPackageProgramList from 'features/settingService/services/hooks/useGetPackageProgramList';

type Props = {
  data: IPackageProgram | null;
  onDeletePackage: (id: string) => void;
};

interface PackageTierFormValues {
  tiers: Array<{
    id: string;
    name: string;
    quantity: number;
    discountType: string;
    discountValue: number;
    sortOrder: number;
  }>;
}

const PackageDetail = ({ data, onDeletePackage }: Props) => {
  const [form] = Form.useForm<PackageTierFormValues>();
  const packageModalRef = useRef<CreatePackageModalRef>(null);
  const tiers = Form.useWatch('tiers', form) || [];
  const [, mutate] = useGetPackageProgramList();
  const tierCalculations = useMemo(() => {
    return tiers.map((tier) => {
      return calculateTier(
        data?.price || 0,
        tier?.quantity,
        tier?.discountType,
        tier?.discountValue,
      );
    });
  }, [data?.price, tiers]);

  useEffect(() => {
    if (!data) return;
    form.setFieldValue('tiers', data.tiers);
  }, [data]);

  const handleFinish = async (values: PackageTierFormValues) => {
    try {
      const res: IResponseDataBody = await settingApis.updatePackageProgram({
        id: data?.id || '',
        body: {
          name: data?.name || '',
          description: data?.description || '',
          price: data?.price || 0,
          validityMonths: data?.validityMonths || 0,
          tiers: values.tiers,
        },
      });

      if (res?.data?.data) {
        toast.success('Update package successfully');
        mutate();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Update package failed');
    }
  };

  const onReset = () => {
    form.resetFields();
    if (!data) return;
    form.setFieldValue('tiers', data.tiers);
  };

  const onSubmit = () => form.submit();

  return (
    <Container>
      <Styled className='category-detail-card'>
        <DetailHeader
          data={data}
          onEditPackage={() => packageModalRef.current?.open(data)}
          onDeletePackage={onDeletePackage}
        />
        <Form
          layout='vertical'
          className='package-detail-form'
          form={form}
          onFinish={handleFinish}
        >
          <div className='content'>
            <Form.List name='tiers'>
              {(fields, { add, remove }) => (
                <>
                  {fields.length === 0 && (
                    <div className='children-container-empty'>
                      <Empty description='Empty service' />
                    </div>
                  )}
                  <SectionHeader>
                    <Box display='flex' flexDirection='column' gap='1'>
                      <Text variant='H5'>Package Options (Tiers)</Text>
                      <Text variant='CONTENT_1' color='text_3_v2'>
                        Set sessions and discount. Prices update automatically.
                      </Text>
                    </Box>
                    <Button
                      ntype='GREEN'
                      cardCashier
                      onClick={() =>
                        add({
                          name: '',
                          quantity: 1,
                          discountType: 'PERCENT',
                          discountValue: 0,
                          type: PACKAGE_TYPE.BRONZE,
                        })
                      }
                      iconElement={
                        <Add
                          fill={colorTheme.v2_text_1}
                          width='40'
                          height='40'
                        />
                      }
                      style={{ height: '4rem' }}
                    >
                      Add Tier
                    </Button>
                  </SectionHeader>
                  {fields.map((field, index) => {
                    const calculation = tierCalculations[index] || {
                      packagePrice: 0,
                      pricePerSession: 0,
                      savings: 0,
                    };

                    return (
                      <PackageTierCard
                        key={field.key}
                        field={field}
                        remove={remove}
                        calculation={calculation}
                      />
                    );
                  })}
                </>
              )}
            </Form.List>
          </div>
        </Form>
        <div className='card-footer'>
          <Button width='10rem' height={3} ntype='SECONDARY' onClick={onReset}>
            Reset
          </Button>
          <Button width='10rem' height={3} ntype='PRIMARY' onClick={onSubmit}>
            Save
          </Button>
        </div>
      </Styled>
      <CreatePackageModal ref={packageModalRef} />
    </Container>
  );
};

export default PackageDetail;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  .category-detail-card {
    flex: 1;
  }
`;

const Styled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .package-detail-form {
    flex: 1;
    margin-right: 16px;
  }
  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 24px;
    background: #ffffff;
    border-radius: 12px;
  }

  .children-container-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .card-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px 24px;
    background: #fff;
    box-shadow: 6px 0px 12px -4px rgba(0, 0, 0, 0.2);
    gap: 24px;
  }

  .tier-form-row {
    grid-template-columns: 1fr 0.8fr 1fr 0.8fr 2fr 0.3fr;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  font-family: Poppins;

  .GREEN {
    padding: 0 24px;
  }
  .CONTENT_2 {
    font-size: 20px;
    font-family: Poppins;
  }
`;
